/**
 * The confirmation before saving quality weights (#874, spec §8, owner
 * decision O3): what will be recalculated, what will not, and the preview on
 * the project's recent calls. Calls that lose their score and calls whose
 * quality band changes are named above the buttons, so the save cannot be
 * confirmed without seeing them — nor without a preview at all: Confirm waits
 * for a successful one, and a failed one can be retried — unless the grant
 * lacks the quality figures it shows, which no retry brings.
 */

import { useTranslation } from 'react-i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import type {
  QualityWeights,
  QualityWeightsPreviewResponse,
} from '@aica/api-types/schemas';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/fetch-utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@/components/ui/modal';

interface QualityWeightsConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preview: Pick<
    UseMutationResult<QualityWeightsPreviewResponse, Error, QualityWeights | null>,
    'data' | 'isPending' | 'isError' | 'error'
  >;
  onRetryPreview: () => void;
  isSaving: boolean;
  onConfirm: () => void;
}

export function QualityWeightsConfirmDialog({
  open,
  onOpenChange,
  preview,
  onRetryPreview,
  isSaving,
  onConfirm,
}: QualityWeightsConfirmDialogProps) {
  const { t, i18n } = useTranslation('settings');
  const score = new Intl.NumberFormat(i18n.language, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const result = preview.data;
  // A grant without the quality figures the preview shows: no retry brings
  // them, and without them there is no save (O3) — say so instead.
  const narrowGrant =
    preview.error instanceof ApiError &&
    (preview.error.data as { code?: unknown } | null | undefined)?.code ===
      'capability_required';
  const average = (value: number | null) =>
    value === null ? t('qualityTab.weights.preview.noScore') : score.format(value);

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="lg" data-testid="quality-weights-confirm">
        <ModalHeader>
          <ModalTitle>{t('qualityTab.weights.confirm.title')}</ModalTitle>
          <ModalDescription>{t('qualityTab.weights.confirm.rescored')}</ModalDescription>
        </ModalHeader>

        <div className="space-y-3 text-xs">
          {preview.isPending && (
            <p className="text-fg-muted" data-testid="quality-weights-preview-loading">
              {t('qualityTab.weights.preview.loading')}
            </p>
          )}
          {preview.isError && narrowGrant && (
            <p
              className="text-warning"
              data-testid="quality-weights-preview-narrow-grant"
            >
              {t('qualityTab.weights.preview.narrowGrant')}
            </p>
          )}
          {preview.isError && !narrowGrant && (
            <div
              className="flex items-center justify-between gap-3"
              data-testid="quality-weights-preview-error"
            >
              <p className="text-warning">{t('qualityTab.weights.preview.error')}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={onRetryPreview}
                data-testid="quality-weights-preview-retry"
              >
                {t('qualityTab.weights.preview.retry')}
              </Button>
            </div>
          )}
          {result && result.scoredCallsNow === 0 && result.scoredCallsNew === 0 && (
            <p className="text-fg-muted" data-testid="quality-weights-preview-empty">
              {t('qualityTab.weights.preview.empty', { days: result.windowDays })}
            </p>
          )}
          {result && (result.scoredCallsNow > 0 || result.scoredCallsNew > 0) && (
            <div data-testid="quality-weights-preview">
              <div className="mb-2 font-medium text-fg">
                {t('qualityTab.weights.preview.title', { days: result.windowDays })}
              </div>
              <table className="w-full">
                <thead>
                  <tr className="text-left text-fg-muted">
                    <th className="pb-1 font-medium">
                      {t('qualityTab.weights.preview.metric')}
                    </th>
                    <th className="pb-1 font-medium text-right">
                      {t('qualityTab.weights.preview.now')}
                    </th>
                    <th className="pb-1 font-medium text-right">
                      {t('qualityTab.weights.preview.new')}
                    </th>
                  </tr>
                </thead>
                <tbody className="font-mono tabular-nums">
                  <tr className="border-t border-border">
                    <td className="py-1 font-sans text-fg-secondary">
                      {t('qualityTab.weights.preview.scored')}
                    </td>
                    <td
                      className="py-1 text-right text-fg"
                      data-testid="preview-scored-now"
                    >
                      {result.scoredCallsNow}
                    </td>
                    <td
                      className="py-1 text-right text-fg"
                      data-testid="preview-scored-new"
                    >
                      {result.scoredCallsNew}
                    </td>
                  </tr>
                  <tr className="border-t border-border">
                    <td className="py-1 font-sans text-fg-secondary">
                      {t('qualityTab.weights.preview.average')}
                    </td>
                    <td className="py-1 text-right text-fg" data-testid="preview-avg-now">
                      {average(result.avgScoreNow)}
                    </td>
                    <td className="py-1 text-right text-fg" data-testid="preview-avg-new">
                      {average(result.avgScoreNew)}
                    </td>
                  </tr>
                  <tr className="border-t border-border">
                    <td className="py-1 font-sans text-fg-secondary">
                      {t('qualityTab.weights.preview.below', {
                        threshold: result.threshold,
                      })}
                    </td>
                    <td className="py-1 text-right text-fg">
                      {result.belowThresholdNow}
                    </td>
                    <td className="py-1 text-right text-fg">
                      {result.belowThresholdNew}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <p className="text-fg-secondary">{t('qualityTab.weights.confirm.rules')}</p>
          <p className="text-fg-secondary">{t('qualityTab.weights.confirm.unchanged')}</p>
          <p className="text-fg-secondary">{t('qualityTab.weights.confirm.refresh')}</p>

          {result && result.losingScore > 0 && (
            <Alert variant="warning" data-testid="quality-weights-losing">
              <AlertDescription>
                {t('qualityTab.weights.confirm.losing', { count: result.losingScore })}
              </AlertDescription>
            </Alert>
          )}
          {result && result.bandChanges > 0 && (
            <Alert variant="info" data-testid="quality-weights-bands">
              <AlertDescription>
                {t('qualityTab.weights.confirm.bands', {
                  count: result.bandChanges,
                  days: result.windowDays,
                })}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <ModalFooter>
          <Button
            variant="ghost"
            size="sm"
            disabled={isSaving}
            onClick={() => onOpenChange(false)}
          >
            {t('qualityTab.weights.confirm.cancel')}
          </Button>
          <Button
            size="sm"
            isLoading={isSaving}
            // The preview is mandatory (spec §8, O3): no numbers, no save.
            disabled={!result}
            onClick={onConfirm}
            data-testid="quality-weights-confirm-submit"
          >
            {t('qualityTab.weights.confirm.submit')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
